<template>
  <PainelLayout>
    <Breadcrumb :lista="[
      { 'name':'Painel', 'link' : '/' }]"
    />

    <div class="row">
      <div class="col-12">
        <h1>Administração</h1>
        <p class="text-muted mb-0">Todas as empresas que usam o AlugUp, suas assinaturas e renovações.</p>
      </div>
    </div>
    <hr/>

    <div v-if="loading" class="d-flex justify-content-center p-3">
      <div class="spinner-border" style="width: 4rem; height: 4rem;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <template v-else-if="resumo">
      <!-- Resumo -->
      <div class="row g-3 mb-4">
        <div v-for="c in cards" :key="c.key" class="col-6 col-md-4 col-xl-2">
          <button type="button" class="card h-100 w-100 text-start adm-card" :class="{ 'border-primary': filtroEstado === c.key }"
            @click="selecionarCard(c.key)" :disabled="c.key === 'revenue'">
            <div class="card-body py-3">
              <div class="small text-muted">{{ c.label }}</div>
              <div class="h4 mb-0">{{ c.valor }}</div>
            </div>
          </button>
        </div>
      </div>

      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <button class="nav-link" :class="{ active: aba === 'clientes' }" @click="aba = 'clientes'">Clientes</button>
        </li>
        <li class="nav-item">
          <button class="nav-link" :class="{ active: aba === 'renovacoes' }" @click="abrirRenovacoes">Renovações</button>
        </li>
      </ul>

      <!-- Clientes -->
      <template v-if="aba === 'clientes'">
        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <input v-model="busca" type="search" class="form-control" placeholder="Buscar por empresa, responsável, e-mail ou WhatsApp" aria-label="Buscar">
          </div>
          <div class="col-md-3">
            <select v-model="filtroEstado" class="form-select" aria-label="Situação">
              <option value="">Todas as situações</option>
              <option v-for="(e, key) in ESTADOS" :key="key" :value="key">{{ e.label }}</option>
            </select>
          </div>
          <div class="col-md-3 text-md-end small text-muted align-self-center">
            {{ filtrados.length }} de {{ empresas.length }} empresas
          </div>
        </div>

        <app-table :empty="filtrados.length === 0" empty-message="Nenhuma empresa encontrada."
          :colunas="[{ label: 'Empresa' }, { label: 'Contato' }, { label: 'Situação' }, { label: 'Vencimento' },
                     { label: 'Itens', align: 'right' }, { label: 'Locações', align: 'right' }, { label: 'Total pago', align: 'right' }, { label: 'Cadastro' }]">
          <app-trow v-for="t in filtrados" :key="t.id" :to="'/admin/client?id=' + t.id" :data="[
            { type: 'avatar', value: t.name, sub: t.owner?.name },
            t.owner?.email || t.whatsapp || '—',
            badgeEstado(t.state),
            vencimento(t),
            { value: t.items_count, align: 'right' },
            { value: t.rentals_count, align: 'right' },
            { type: 'currency', value: t.total_paid, align: 'right' },
            data(t.created_at)
          ]" />
        </app-table>
      </template>

      <!-- Renovações -->
      <template v-else>
        <div class="row g-2 mb-3">
          <div class="col-md-3">
            <select v-model="filtroPagamento" class="form-select" aria-label="Status do pagamento" @change="carregarPagamentos">
              <option value="">Todos os status</option>
              <option v-for="(p, key) in PAGAMENTOS" :key="key" :value="key">{{ p.label }}</option>
            </select>
          </div>
        </div>
        <div v-if="carregandoPagamentos" class="d-flex justify-content-center p-3">
          <div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>
        </div>
        <app-table v-else :empty="pagamentos.length === 0" empty-message="Nenhum pagamento ainda."
          :colunas="[{ label: 'Data' }, { label: 'Empresa' }, { label: 'Período' }, { label: 'Valor', align: 'right' }, { label: 'Status' }, { label: 'Pago em' }]">
          <app-trow v-for="p in pagamentos" :key="p.id" :to="'/admin/client?id=' + p.id_tenant" :data="[
            dataHora(p.created_at),
            p.tenant_name || ('#' + p.id_tenant),
            periodo(p.months),
            { type: 'currency', value: p.amount, align: 'right' },
            badgePagamento(p.status),
            dataHora(p.paid_at)
          ]" />
        </app-table>
      </template>
    </template>
  </PainelLayout>
</template>

<script>
import PainelLayout from '@/layouts/PainelLayout.vue';
import { onMounted, ref, computed } from 'vue';
import { admin } from '@/models/admin';
import Breadcrumb from '@/components/itens/breadcrumb.vue';
import AppTable from '@/components/AppTable.vue';
import AppTrow from '@/components/AppTrow.vue';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';
import { ESTADOS, PAGAMENTOS, badgeEstado, badgePagamento, data, dataHora, money, periodo, vencimento } from './adminHelpers';

export default {
  name: "AdminView",
  components: { PainelLayout, Breadcrumb, AppTable, AppTrow },
  setup() {

    const toastStore = useToastStore();
    const model = admin();
    const loading = ref(false);
    const resumo = ref(null);
    const empresas = ref([]);
    const busca = ref('');
    const filtroEstado = ref('');
    const aba = ref('clientes');
    const pagamentos = ref([]);
    const filtroPagamento = ref('');
    const carregandoPagamentos = ref(false);
    let pagamentosCarregados = false;

    const cards = computed(() => {
      const r = resumo.value;
      return [
        { key: 'all',     label: 'Empresas',       valor: r.total },
        { key: 'trial',   label: 'Em teste',       valor: r.trial },
        { key: 'active',  label: 'Pagantes',       valor: r.active },
        { key: 'expired', label: 'Vencidas',       valor: r.expired },
        { key: 'legacy',  label: 'Legado',         valor: r.legacy },
        { key: 'revenue', label: 'Recebido (30 dias)', valor: money(r.revenue_30d) },
      ];
    });

    const filtrados = computed(() => {
      const q = busca.value.trim().toLowerCase();
      return empresas.value.filter(t => {
        if (filtroEstado.value && t.state !== filtroEstado.value) return false;
        if (!q) return true;
        return [t.name, t.slug, t.whatsapp, t.owner?.name, t.owner?.email, t.owner?.phone]
          .some(v => String(v || '').toLowerCase().includes(q));
      });
    });

    async function carregar() {
      loading.value = true;
      try {
        const res = await model.list();
        resumo.value = res.summary;
        empresas.value = res.tenants;
      } catch (error) {
        toastStore.error('Erro ao carregar empresas: ' + parseError(error));
      }
      loading.value = false;
    }

    async function carregarPagamentos() {
      carregandoPagamentos.value = true;
      try {
        pagamentos.value = await model.payments(filtroPagamento.value ? { status: filtroPagamento.value } : {});
        pagamentosCarregados = true;
      } catch (error) {
        toastStore.error('Erro ao carregar pagamentos: ' + parseError(error));
      }
      carregandoPagamentos.value = false;
    }

    // Card de situação funciona como filtro (clicar de novo, ou em "Empresas", limpa)
    function selecionarCard(key) {
      aba.value = 'clientes';
      filtroEstado.value = key === 'all' || filtroEstado.value === key ? '' : key;
    }

    function abrirRenovacoes() {
      aba.value = 'renovacoes';
      if (!pagamentosCarregados) carregarPagamentos();
    }

    onMounted(carregar);

    return {
      loading, resumo, empresas, busca, filtroEstado, aba, pagamentos, filtroPagamento, carregandoPagamentos,
      cards, filtrados, carregarPagamentos, abrirRenovacoes, selecionarCard,
      ESTADOS, PAGAMENTOS, badgeEstado, badgePagamento, data, dataHora, periodo, vencimento
    }
  }
}
</script>

<style scoped>
.adm-card {
  background: var(--bs-body-bg);
}

.adm-card:disabled {
  opacity: 1;
}
</style>
